import {
    useInvalidateRepositoryQuery,
    PaymentTerminalCommandType,
    useRepositoryMutation,
    useRepositorySuspenseQuery,
    GatewayAuthorizationType,
    AuthorizationType,
    PreauthorizationMethod,
    PaymentRequestType,
    GatewayPaymentMethod,
} from '@mews-commander/data-access';
import { WithAccountId } from '@mews-commander/types';
import { useFeatureFlag } from '@mews/framework';
import { FeatureFlag } from '@mews-commander/enums';
import { UNSPECIFIED_CREDIT_CARD_VALUE } from '@mews-commander/legacy-entities/src/ducks/payments';
import { useGetExpirationUtcWithTimeNow } from './hooks';
import { GatewayPreauthorizationFormValues } from './GatewayPreauthorizationForm/schema';
import { ExternalPreauthorizationFormValues } from './ExternalPreauthorizationForm/schema';
import { MewsTerminalPreauthorizationFormValues } from './MewsTerminalPreauthorizationForm/schema';
import { GatewayPaymentFormValues } from './GatewayPaymentForm/schema';
import { hasPaymentMethodId } from './GatewayPaymentForm/utils';
import { ExternalCardPaymentFormValues } from './ExternalCardPaymentForm/schema';
import { MewsTerminalPaymentFormValues } from './MewsTerminalPayment/schema';
import { UpdatePreauthorizationFormValues } from './UpdatePreauthorizationForm/schema';
import { ChargePreauthorizationFormValues } from './ChargePreauthorizationForm/schema';
import {
    PaymentRequestTypePaymentFormValues,
    PaymentRequestTypePreauthorizationFormValues,
} from './PaymentRequestForm/schema';

export const useGetSupportedGatewayCardTypesSuspense = () =>
    useRepositorySuspenseQuery('supportedGatewayCardTypes', 'get', {});

export const useGetChargeablePreauthorizationSuspense = ({
    accountId,
    preauthorizationId,
}: { preauthorizationId: string } & WithAccountId) =>
    useRepositorySuspenseQuery(
        'accountChargeablePreauthorizations',
        'getAll',
        { accountId, preauthorizationId },
        {
            select: (result) => result.find(({ id }) => id === preauthorizationId),
        }
    );

export const useInvalidateChargeablePreauthorizationQuery = ({
    accountId,
    preauthorizationId,
}: { preauthorizationId: string } & WithAccountId) => {
    const invalidateRepositoryQuery = useInvalidateRepositoryQuery();

    return () =>
        void invalidateRepositoryQuery('chargeablePreauthorizations', 'getAll', {
            customerId: accountId,
            preauthorizationId,
        });
};
export const useAddGatewayPreauthorization = ({ accountId }: WithAccountId) => {
    const { mutateAsync } = useRepositoryMutation('gatewayPreauthorizations', 'add');
    const getExpirationUtcWithTimeNow = useGetExpirationUtcWithTimeNow();

    return async (formValues: GatewayPreauthorizationFormValues) => {
        const { paymentMethodId, amount, currencyCode, notes, serviceOrderId, expirationUtc } = formValues;

        return await mutateAsync({
            accountId,
            creditCardId: paymentMethodId,
            serviceOrderId,
            amount,
            currency: currencyCode,
            notes,
            expirationUtc: getExpirationUtcWithTimeNow(expirationUtc),
        });
    };
};

export const useAddNonGatewayPreauthorization = ({ accountId }: WithAccountId) => {
    const { mutateAsync } = useRepositoryMutation('nonGatewayPreauthorizations', 'add');

    return async (formValues: ExternalPreauthorizationFormValues) => {
        const { paymentMethodId, serviceOrderId, amount, currencyCode, notes, code, receiptIdentifier, sequenceCode } =
            formValues;
        await mutateAsync({
            accountId,
            creditCardId: paymentMethodId,
            serviceOrderId,
            amount,
            currency: currencyCode,
            notes,
            code,
            receiptIdentifier,
            sequenceCode,
        });
    };
};

export const useAddTerminalPreauthorization = ({ accountId }: WithAccountId) => {
    const { mutateAsync } = useRepositoryMutation('paymentTerminals', 'addPreauthorization');

    return async (formValues: MewsTerminalPreauthorizationFormValues) => {
        const { amount, currencyCode, paymentTerminalId, expirationUtc, serviceOrderId, notes } = formValues;

        return await mutateAsync({
            accountId,
            paymentTerminalId,
            serviceOrderId,
            amount: {
                currencyCode,
                value: amount,
            },
            notes,
            expirationUtc,
        });
    };
};

export const useAddGatewayCardPayment = ({ accountId }: WithAccountId) => {
    const { mutateAsync } = useRepositoryMutation('gatewayCardPayments', 'add');

    return async (formValues: GatewayPaymentFormValues, paymentCardId: string, preauthorizationId: string) => {
        const { serviceOrderId, billId, invoiceIds, notes, paymentPlanId, currencyCode, amount } = formValues;

        const simpleAmount = { currencyCode, value: amount };

        return await mutateAsync({
            accountId,
            serviceOrderId,
            billId,
            invoiceIds,
            notes,
            paymentPlanId,
            data: paymentCardId
                ? {
                      discriminator: GatewayAuthorizationType.CardPayment,
                      value: {
                          cardId: paymentCardId,
                          amount: simpleAmount,
                          applySurcharge: hasPaymentMethodId(formValues)
                              ? !!formValues.applyPaymentCardSurcharge
                              : false,
                      },
                  }
                : {
                      discriminator: GatewayAuthorizationType.Preauthorization,
                      value: {
                          preauthorizationId,
                          desiredAmount: simpleAmount,
                      },
                  },
        });
    };
};

export const useAddNonGatewayCardPayment = ({ accountId }: WithAccountId) => {
    const { mutateAsync } = useRepositoryMutation('nonGatewayCardPayments', 'add');

    return async (formValues: ExternalCardPaymentFormValues, paymentCardId: string, preauthorizationId: string) => {
        const { serviceOrderId, billId, invoiceIds, notes, paymentPlanId, currencyCode, amount, receiptIdentifier } =
            formValues;

        const simpleAmount = { currencyCode, value: amount };

        return await mutateAsync({
            accountId,
            serviceOrderId,
            billId,
            invoiceIds,
            notes,
            paymentPlanId,
            data: paymentCardId
                ? {
                      discriminator: AuthorizationType.CreditCardPayment,
                      value: {
                          cardId: paymentCardId === UNSPECIFIED_CREDIT_CARD_VALUE ? null : paymentCardId,
                          amount: simpleAmount,
                      },
                  }
                : {
                      discriminator: AuthorizationType.Preauthorization,
                      value: {
                          preauthorizationId,
                          desiredAmount: simpleAmount,
                      },
                  },
            receiptIdentifier,
        });
    };
};

export const useAddMewsTerminalPayment = ({ accountId }: WithAccountId) => {
    const isAccountPaymentTerminalsAddPaymentEndpointEnabled = useFeatureFlag(
        FeatureFlag.accountPaymentTerminalsAddPaymentEndpointEnabled
    );
    const { mutateAsync: mutateAddCustomerTerminalPayment } = useRepositoryMutation('paymentTerminals', 'addPayment');
    const { mutateAsync: mutateAddAccountTerminalPayment } = useRepositoryMutation(
        'paymentTerminals',
        'addAccountPayment'
    );

    return async (formValues: MewsTerminalPaymentFormValues) => {
        const { currencyCode, notes, paymentTerminalId, amount, billId, invoiceIds, serviceOrderId, paymentPlanId } =
            formValues;

        if (isAccountPaymentTerminalsAddPaymentEndpointEnabled) {
            return await mutateAddAccountTerminalPayment({
                accountId,
                paymentTerminalId,
                billId,
                invoiceIds,
                serviceOrderId,
                amount: {
                    value: amount,
                    currencyCode,
                },
                paymentPlanId,
                notes,
            });
        }

        return await mutateAddCustomerTerminalPayment({
            customerId: accountId,
            paymentTerminalId,
            billId,
            invoiceIds,
            serviceOrderId,
            value: amount,
            currencyCode,
            type: PaymentTerminalCommandType.Payment,
            notes,
        });
    };
};

export const useUpdatePreauthorization = ({
    preauthorizationId,
    accountId,
}: { preauthorizationId: string } & WithAccountId) => {
    const { mutateAsync } = useRepositoryMutation('gatewayPreauthorizations', 'update');

    return async (formValues: UpdatePreauthorizationFormValues) => {
        const { amount, notes, serviceOrderId } = formValues;

        return await mutateAsync({
            amount,
            accountId,
            preauthorizationId,
            notesToAppend: notes,
            serviceOrderId,
        });
    };
};

export const useGetChargeablePreauthorizationsSuspense = ({
    accountId,
    isGateway,
}: { isGateway: boolean } & WithAccountId) =>
    useRepositorySuspenseQuery(
        'accountChargeablePreauthorizations',
        'getAll',
        { accountId },
        {
            select: (result) => result.filter((r) => r.isGateway === isGateway),
        }
    );

export const useChargePreauthorization = ({
    accountId,
    preauthorizationId,
}: { preauthorizationId: string } & WithAccountId) => {
    const { mutateAsync: chargeGatewayAccountPreauthorization } = useRepositoryMutation('gatewayCardPayments', 'add');
    const { mutateAsync: chargeNonGatewayAccountPreauthorization } = useRepositoryMutation(
        'nonGatewayCardPayments',
        'add'
    );

    return async (formValues: ChargePreauthorizationFormValues) => {
        const { amount, notes, serviceOrderId, currencyCode, isGateway, billId, invoiceIds, paymentPlanId } =
            formValues;
        const simpleAmount = { currencyCode, value: amount };
        if (isGateway) {
            return await chargeGatewayAccountPreauthorization({
                accountId,
                serviceOrderId,
                billId,
                invoiceIds,
                notes,
                paymentPlanId,
                data: {
                    discriminator: GatewayAuthorizationType.Preauthorization,
                    value: {
                        preauthorizationId,
                        desiredAmount: simpleAmount,
                    },
                },
            });
        }
        return await chargeNonGatewayAccountPreauthorization({
            accountId,
            serviceOrderId,
            billId,
            invoiceIds,
            notes,
            paymentPlanId,
            data: {
                discriminator: AuthorizationType.Preauthorization,
                value: {
                    preauthorizationId,
                    desiredAmount: simpleAmount,
                },
            },
            receiptIdentifier: formValues.receiptIdentifier,
        });
    };
};

export const useAddPaymentRequest = ({ accountId }: WithAccountId) => {
    const { mutateAsync: mutateAddAccountPaymentRequest } = useRepositoryMutation('paymentRequests', 'add');

    return async (formValues: PaymentRequestTypePaymentFormValues) => {
        const {
            serviceOrderId,
            amount,
            currencyCode,
            notes,
            billId,
            reason,
            expirationUtc,
            description,
            paymentPlanId,
            email,
            methods,
        } = formValues;

        await mutateAddAccountPaymentRequest({
            accountId,
            serviceOrderId,
            billId,
            amount: {
                currencyCode,
                value: amount,
            },
            reason,
            expirationUtc,
            description,
            notes,
            paymentPlanId,
            email: email ?? '', // TODO: remove fallback when paymentRequestCollectTypeEnabled FF is removed
            paymentMethods: methods ?? [], // TODO: remove fallback when paymentRequestCollectTypeEnabled FF is removed
        });
    };
};

export const useGetAccountEmailSuspense = ({ accountId }: WithAccountId) =>
    useRepositorySuspenseQuery(
        'accounts',
        'getAll',
        { accountIds: [accountId] },
        {
            select: (result) => result.accounts?.find((r) => r.id === accountId)?.data.value.email,
        }
    );

export const useAddPreauthorizationRequest = ({ accountId }: WithAccountId) => {
    const { mutateAsync } = useRepositoryMutation('preauthorizationRequests', 'add');

    return async (formValues: PaymentRequestTypePreauthorizationFormValues) => {
        const { serviceOrderId, amount, currencyCode, notes, expirationUtc, description, email } = formValues;
        await mutateAsync({
            serviceOrderId,
            amount: {
                value: amount,
                currencyCode,
            },
            expirationUtc,
            description,
            notes,
            email,
            accountId,
            preauthorizationMethods: [PreauthorizationMethod.PaymentCard],
        });
    };
};

export const useGetPaymentRequestSupportedPaymentMethods = ({
    paymentRequestType,
}: {
    paymentRequestType: PaymentRequestType;
}) =>
    useRepositorySuspenseQuery(
        'paymentRequests',
        'getSupportedPaymentMethods',
        { paymentRequestType },
        {
            select: (result) => {
                const { applePay, googlePay, ideal, paymentCard, sepaDirectDebit } = result;

                return [
                    ...(sepaDirectDebit ? [GatewayPaymentMethod.SepaDirectDebit] : []),
                    ...(paymentCard ? [GatewayPaymentMethod.PaymentCard] : []),
                    ...(ideal ? [GatewayPaymentMethod.Ideal] : []),
                    ...(applePay ? [GatewayPaymentMethod.ApplePay] : []),
                    ...(googlePay ? [GatewayPaymentMethod.GooglePay] : []),
                ];
            },
        }
    );
